import { useState, useEffect } from "react";
import { Card } from "./components.js";
import {
  getSalesReport,
  getRevenueTrends,
  getCustomerAnalytics,
  getProductAnalytics,
  getPaymentMethodAnalytics,
  getRevenueForecast,
  getOperationalMetrics,
  getProfitabilityAnalysis
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// SALES REPORT COMPONENT (EXCLUSIVE TIER ONLY)
// ══════════════════════════════════════════════════════════════════════════════

export default function SalesReport({ G, brand, user }) {
  // ─────────────────────────────────────────────────────────────────────
  // STATE MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────
  
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Date filters
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Data state
  const [salesReport, setSalesReport] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState(null);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);
  const [productAnalytics, setProductAnalytics] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [operationalMetrics, setOperationalMetrics] = useState(null);
  
  // Email modal
  const [customEmail, setCustomEmail] = useState(brand?.email || "");
  const [emailSent, setEmailSent] = useState(false);
  
  // Check if user has access
  const isExclusiveTier = user?.tierLevel >= 2;
  
  // ─────────────────────────────────────────────────────────────────────
  // LOAD DATA
  // ─────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (!isExclusiveTier) return; // Don't load data for non-exclusive users
    
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, isExclusiveTier]);
    const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filters = { fromDate, toDate };
      
      // Load all analytics in parallel
      const results = await Promise.allSettled([
        getSalesReport({ ...filters, compareWithPreviousPeriod: true }),
        getRevenueTrends({ ...filters, groupBy: 'day' }),
        getCustomerAnalytics({ ...filters, limit: 10 }),
        getProductAnalytics({ ...filters, limit: 10 }),
        getPaymentMethodAnalytics(filters),
        getRevenueForecast({ periods: 3, method: 'linear-regression' }),
        getProfitabilityAnalysis(filters),
        getOperationalMetrics(filters)
      ]);
      
      // ✅ SAFELY EXTRACT RESULTS (handle rejections gracefully)
      const salesData = results[0].status === 'fulfilled' ? results[0].value : null;
      const trendsData = results[1].status === 'fulfilled' ? results[1].value : null;
      const customersData = results[2].status === 'fulfilled' ? results[2].value : null;
      const productsData = results[3].status === 'fulfilled' ? results[3].value : null;
      const paymentsData = results[4].status === 'fulfilled' ? results[4].value : null;
      const forecastData = results[5].status === 'fulfilled' ? results[5].value : { 
        forecast: [], 
        confidence: { level: 0 },
        summary: { expectedGrowth: 0 },
        insights: [{ 
          type: 'info', 
          title: 'No Data Yet', 
          message: 'Create invoices to see revenue forecasts',
          action: 'Start invoicing to build forecast history'
        }]
      };
      const profitData = results[6].status === 'fulfilled' ? results[6].value : null;
      const opsData = results[7].status === 'fulfilled' ? results[7].value : null;
      
      setSalesReport(salesData);
      setRevenueTrends(trendsData);
      setCustomerAnalytics(customersData);
      setProductAnalytics(productsData);
      setPaymentAnalytics(paymentsData);
      setForecast(forecastData);
      setProfitability(profitData);
      setOperationalMetrics(opsData);
      
      // ✅ LOG INFORMATIONAL MESSAGE (not error)
      if (salesData?.current?.invoiceCount === 0) {
        console.log('ℹ️ No invoice data yet - showing empty state');
      }
      
    } catch (err) {
      console.error('Failed to load sales report:', err);
      
      // ✅ ONLY SHOW ERROR FOR UNEXPECTED FAILURES
      if (err.message?.includes('network') || 
          err.message?.includes('authentication') ||
          err.message?.includes('permission')) {
        setError(err.message);
      } else {
        console.warn('Sales report loaded with partial data');
      }
    } finally {
      setLoading(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────
  // EMAIL REPORT HANDLER
  // ─────────────────────────────────────────────────────────────────────
  
  const handleEmailReport = () => {
    if (!customEmail || !salesReport) return;
    
    const subject = encodeURIComponent(`Sales Report: ${fromDate} to ${toDate}`);
    const body = encodeURIComponent(
      `Sales Report Summary\n` +
      `Period: ${fromDate} to ${toDate}\n\n` +
      `Total Revenue: $${(salesReport.current.totalRevenue / 100).toFixed(2)}\n` +
      `Invoices: ${salesReport.current.invoiceCount}\n` +
      `Average Invoice Value: $${(salesReport.current.averageInvoiceValue / 100).toFixed(2)}\n` +
      `Payment Rate: ${salesReport.current.paymentRate.toFixed(2)}%\n\n` +
      `Generated from ${brand.name || 'Invoice Generator'}`
    );
    
    window.open(`mailto:${customEmail}?subject=${subject}&body=${body}`);
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };
  
  // ─────────────────────────────────────────────────────────────────────
  // HELPER FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────
  
  const formatCurrency = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };
  
  const getTrendIcon = (trend) => {
    if (trend === 'up') return '📈';
    if (trend === 'down') return '📉';
    return '➡️';
  };
  
  const getTrendColor = (trend) => {
    if (trend === 'up') return '#22C55E';
    if (trend === 'down') return '#EF4444';
    return G.muted;
  };
  
  const getInsightIcon = (type) => {
    if (type === 'positive') return '✅';
    if (type === 'warning') return '⚠️';
    if (type === 'opportunity') return '💡';
    return 'ℹ️';
  };
  
  const getInsightColor = (type) => {
    if (type === 'positive') return '#22C55E';
    if (type === 'warning') return '#EAB308';
    if (type === 'opportunity') return '#3B82F6';
    return G.muted;
  };
  
  // ─────────────────────────────────────────────────────────────────────
  // TABS CONFIGURATION
  // ─────────────────────────────────────────────────────────────────────
  
  const tabs = [
    { id: 'overview', label: '📊 Overview', data: salesReport },
    { id: 'trends', label: '📈 Revenue Trends', data: revenueTrends },
    { id: 'customers', label: '👥 Customers', data: customerAnalytics },
    { id: 'products', label: '📦 Products', data: productAnalytics },
    { id: 'payments', label: '💳 Payments', data: paymentAnalytics },
    { id: 'profitability', label: '💰 Profitability', data: profitability },
    { id: 'forecast', label: '🔮 Forecast', data: forecast },
    { id: 'operations', label: '⚙️ Operations', data: operationalMetrics }
  ];
  
  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER: NON-EXCLUSIVE TIER (Empty State)
  // ══════════════════════════════════════════════════════════════════════════════
  
  if (!isExclusiveTier) {
    return (
      <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
        {/* Header */}
        <div style={{ marginBottom: 22 }}>
          <h2 style={{ 
            fontFamily: "'Lora', serif", 
            fontSize: 20, 
            fontWeight: 600, 
            color: G.ink, 
            marginBottom: 4 
          }}>
            📊 Sales Report & Analytics
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            Advanced sales insights and business intelligence
          </div>
        </div>
        
        {/* Exclusive Tier Message */}
        <div style={{
          background: G.surface,
          border: `2px dashed ${G.border}`,
          borderRadius: 12,
          padding: "60px 40px",
          textAlign: "center"
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ 
            fontSize: 18, 
            fontWeight: 600, 
            color: G.ink, 
            marginBottom: 8 
          }}>
            Exclusive Tier Feature
          </div>
          <div style={{ 
            fontSize: 14, 
            color: G.muted, 
            marginBottom: 24,
            lineHeight: 1.6
          }}>
            Advanced sales analytics, revenue forecasting, customer insights, and<br />
            business intelligence reports are available on the Exclusive tier.
          </div>
          <div style={{
            background: G.surface2,
            borderRadius: 8,
            padding: "20px",
            maxWidth: 600,
            margin: "0 auto",
            textAlign: "left"
          }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: G.ink, 
              marginBottom: 12 
            }}>
              Exclusive Tier Includes:
            </div>
            <ul style={{ 
              fontSize: 13, 
              color: G.muted, 
              lineHeight: 1.8,
              paddingLeft: 20
            }}>
              <li>📈 Revenue trends and forecasting</li>
              <li>👥 Customer lifetime value and segmentation</li>
              <li>📦 Product performance analysis</li>
              <li>💳 Payment method analytics</li>
              <li>💰 Profitability and margin analysis</li>
              <li>⚙️ Operational efficiency metrics</li>
              <li>🎯 Actionable business insights</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
  
  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER: EXCLUSIVE TIER (Full Analytics Dashboard)
  // ══════════════════════════════════════════════════════════════════════════════
  
  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-start", 
        marginBottom: 22 
      }}>
        <div>
          <h2 style={{ 
            fontFamily: "'Lora', serif", 
            fontSize: 20, 
            fontWeight: 600, 
            color: G.ink, 
            marginBottom: 4 
          }}>
            📊 Sales Report & Analytics
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            {loading ? "Loading analytics..." : "Comprehensive business intelligence and insights"}
          </div>
        </div>
      </div>
      
      {/* Filters & Actions */}
      <Card G={G} style={{ marginBottom: 16, padding: "16px 20px" }}>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "auto auto auto 1fr auto", 
          gap: 12, 
          alignItems: "center" 
        }}>
          {/* From Date */}
          <div>
            <label style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: G.muted, 
              textTransform: "uppercase", 
              letterSpacing: 0.6,
              display: "block",
              marginBottom: 6
            }}>
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              max={toDate}
              style={{
                padding: "8px 12px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                fontSize: 13,
                fontFamily: "'Sora', sans-serif",
                background: G.surface,
                color: G.ink
              }}
            />
          </div>
          
          {/* To Date */}
          <div>
            <label style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: G.muted, 
              textTransform: "uppercase", 
              letterSpacing: 0.6,
              display: "block",
              marginBottom: 6
            }}>
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              min={fromDate}
              max={new Date().toISOString().split('T')[0]}
              style={{
                padding: "8px 12px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                fontSize: 13,
                fontFamily: "'Sora', sans-serif",
                background: G.surface,
                color: G.ink
              }}
            />
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={loadAllData}
            disabled={loading}
            style={{
              alignSelf: "flex-end",
              padding: "9px 16px",
              background: G.accent,
              color: G.accentFg,
              border: "none",
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "'Sora', sans-serif",
              opacity: loading ? 0.6 : 1,
              whiteSpace: "nowrap"
            }}
          >
            {loading ? "Loading..." : "🔄 Refresh"}
          </button>
          
          {/* Spacer */}
          <div></div>
          
          {/* Email Section */}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div>
              <label style={{ 
                fontSize: 11, 
                fontWeight: 600, 
                color: G.muted, 
                textTransform: "uppercase", 
                letterSpacing: 0.6,
                display: "block",
                marginBottom: 6
              }}>
                Email To
              </label>
              <input
                type="email"
                value={customEmail}
                onChange={e => setCustomEmail(e.target.value)}
                placeholder="owner@business.com"
                style={{
                  padding: "8px 12px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 7,
                  fontSize: 13,
                  fontFamily: "'Sora', sans-serif",
                  background: G.surface,
                  color: G.ink,
                  width: 200
                }}
              />
            </div>
            
            <button
              onClick={handleEmailReport}
              disabled={!customEmail || !salesReport}
              style={{
                background: emailSent ? G.success : G.accent,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "9px 16px",
                fontSize: 13,
                fontWeight: 500,
                cursor: !customEmail || !salesReport ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
                opacity: !customEmail || !salesReport ? 0.45 : 1,
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
                boxShadow: emailSent ? "none" : `0 2px 8px ${G.accent}40`
              }}
            >
              {emailSent ? "✅ Opened!" : "📧 Email Report"}
            </button>
          </div>
        </div>
      </Card>
      
      {/* Error State */}
      {error && (
        <div style={{
          background: G.dangerBg,
          border: `1px solid ${G.danger}`,
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          color: G.danger,
          fontSize: 13
        }}>
          ⚠️ {error}
        </div>
      )}
      
      {/* Tabs */}
      <div style={{ 
        display: "flex", 
        gap: 4, 
        marginBottom: 16,
        overflowX: "auto",
        padding: "4px 0"
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 16px",
              border: `1.5px solid ${activeTab === tab.id ? G.accent : G.border}`,
              borderRadius: 8,
              background: activeTab === tab.id ? G.accentBg : G.surface,
              color: activeTab === tab.id ? G.accent : G.ink2,
              fontSize: 13,
              fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: "pointer",
              fontFamily: "'Sora', sans-serif",
              transition: "all .15s",
              whiteSpace: "nowrap"
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      {loading && (
        <div style={{ 
          textAlign: "center", 
          padding: "60px 20px", 
          color: G.muted 
        }}>
          Loading analytics...
        </div>
      )}
      
      {!loading && (
        <>
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && salesReport && (
            <div>
              {/* Key Metrics */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(4, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                {/* Total Revenue */}
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Total Revenue
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.accent, marginBottom: 8 }}>
                    {formatCurrency(salesReport.current.totalRevenue)}
                  </div>
                  {salesReport.comparison && (
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 6, 
                      fontSize: 12,
                      color: getTrendColor(salesReport.comparison.changes.revenue.trend)
                    }}>
                      <span>{getTrendIcon(salesReport.comparison.changes.revenue.trend)}</span>
                      <span>{salesReport.comparison.changes.revenue.percentage}% vs previous period</span>
                    </div>
                  )}
                </Card>
                
                {/* Invoices */}
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Invoices
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink, marginBottom: 8 }}>
                    {salesReport.current.invoiceCount}
                  </div>
                  {salesReport.comparison && (
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 6, 
                      fontSize: 12,
                      color: getTrendColor(salesReport.comparison.changes.invoiceCount.trend)
                    }}>
                      <span>{getTrendIcon(salesReport.comparison.changes.invoiceCount.trend)}</span>
                      <span>{salesReport.comparison.changes.invoiceCount.percentage}% vs previous period</span>
                    </div>
                  )}
                </Card>
                
                {/* Average Invoice Value */}
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Avg Invoice Value
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink, marginBottom: 8 }}>
                    {formatCurrency(salesReport.current.averageInvoiceValue)}
                  </div>
                  {salesReport.comparison && (
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 6, 
                      fontSize: 12,
                      color: getTrendColor(salesReport.comparison.changes.averageValue.trend)
                    }}>
                      <span>{getTrendIcon(salesReport.comparison.changes.averageValue.trend)}</span>
                      <span>{salesReport.comparison.changes.averageValue.percentage}% vs previous period</span>
                    </div>
                  )}
                </Card>
                
                {/* Payment Rate */}
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Collection Rate
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: salesReport.current.paymentRate > 80 ? G.success : G.danger, marginBottom: 8 }}>
                    {salesReport.current.paymentRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    {formatCurrency(salesReport.current.totalPaid)} collected
                  </div>
                </Card>
              </div>
              
              {/* Revenue Breakdown */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  Revenue Breakdown
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Gross Revenue</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.ink }}>
                      {formatCurrency(salesReport.current.totalRevenue)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Tax Collected</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.ink }}>
                      {formatCurrency(salesReport.current.totalTax)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Discounts Given</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.danger }}>
                      {formatCurrency(salesReport.current.totalDiscounts)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Total Paid</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.success }}>
                      {formatCurrency(salesReport.current.totalPaid)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Refunded</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.danger }}>
                      {formatCurrency(salesReport.current.totalRefunded)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Outstanding</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: G.muted }}>
                      {formatCurrency(salesReport.current.outstanding)}
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Insights */}
              {salesReport.insights && salesReport.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    🎯 Business Insights & Recommendations
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {salesReport.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 Recommended Action: {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
          
          {/* REVENUE TRENDS TAB */}
          {activeTab === 'trends' && revenueTrends && (
            <div>
              {/* Trend Summary */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Trend Direction
                  </div>
                  <div style={{ 
                    fontSize: 20, 
                    fontWeight: 700, 
                    color: getTrendColor(revenueTrends.trend.direction === 'increasing' ? 'up' : revenueTrends.trend.direction === 'decreasing' ? 'down' : 'stable'),
                    marginBottom: 8 
                  }}>
                    {getTrendIcon(revenueTrends.trend.direction === 'increasing' ? 'up' : revenueTrends.trend.direction === 'decreasing' ? 'down' : 'stable')} {revenueTrends.trend.direction.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    {revenueTrends.trend.description}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Average Revenue
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.ink, marginBottom: 8 }}>
                    {formatCurrency(revenueTrends.summary.averageRevenue)}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    per {revenueTrends.groupBy}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Peak Revenue
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.success, marginBottom: 8 }}>
                    {formatCurrency(revenueTrends.summary.highestRevenue)}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    Best: {revenueTrends.peaks.best[0]?.period}
                  </div>
                </Card>
              </div>
              
              {/* Time Series Data */}
              <Card G={G}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  Revenue Over Time ({revenueTrends.groupBy})
                </div>
                <div style={{ 
                  maxHeight: 400, 
                  overflowY: "auto",
                  border: `1px solid ${G.border}`,
                  borderRadius: 8
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ position: "sticky", top: 0, background: G.ink, zIndex: 1 }}>
                      <tr>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Period
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Revenue
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Invoices
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueTrends.data.map((period, idx) => (
                        <tr
                          key={period.period}
                          style={{
                            borderTop: `1px solid ${G.border}`,
                            background: idx % 2 === 0 ? G.surface : G.surface2
                          }}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 500 }}>
                            {period.period}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>
                            {formatCurrency(period.revenue)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted }}>
                            {period.invoiceCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
          
          {/* CUSTOMER ANALYTICS TAB */}
          {activeTab === 'customers' && customerAnalytics && (
            <div>
              {/* Customer Segments */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    VIP Customers
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#FFD700", marginBottom: 8 }}>
                    {customerAnalytics.segments.vip.count}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>
                    {formatCurrency(customerAnalytics.segments.vip.totalRevenue)} revenue
                  </div>
                  <div style={{ fontSize: 11, color: G.muted, fontStyle: "italic" }}>
                    {customerAnalytics.segments.vip.description}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Regular Customers
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.accent, marginBottom: 8 }}>
                    {customerAnalytics.segments.regular.count}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>
                    {formatCurrency(customerAnalytics.segments.regular.totalRevenue)} revenue
                  </div>
                  <div style={{ fontSize: 11, color: G.muted, fontStyle: "italic" }}>
                    {customerAnalytics.segments.regular.description}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Occasional Customers
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.muted, marginBottom: 8 }}>
                    {customerAnalytics.segments.occasional.count}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>
                    {formatCurrency(customerAnalytics.segments.occasional.totalRevenue)} revenue
                  </div>
                  <div style={{ fontSize: 11, color: G.muted, fontStyle: "italic" }}>
                    {customerAnalytics.segments.occasional.description}
                  </div>
                </Card>
              </div>
              
              {/* Top Customers */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  👑 Top Customers by Revenue
                </div>
                <div style={{ 
                  border: `1px solid ${G.border}`,
                  borderRadius: 8,
                  overflow: "hidden"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: G.ink }}>
                      <tr>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Rank
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Customer
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Revenue
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Invoices
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Avg Value
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "center", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Segment
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerAnalytics.topCustomers.map((customer, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderTop: `1px solid ${G.border}`,
                            background: idx % 2 === 0 ? G.surface : G.surface2
                          }}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: idx < 3 ? "#FFD700" : G.muted }}>
                            #{idx + 1}
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 500 }}>
                            {customer.name}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>
                            {formatCurrency(customer.totalRevenue)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted }}>
                            {customer.invoiceCount}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace" }}>
                            {formatCurrency(customer.averageInvoiceValue)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <span style={{
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              background: customer.segment === 'vip' ? "#FFD70020" : customer.segment === 'regular' ? G.accentBg : G.surface2,
                              color: customer.segment === 'vip' ? "#FFD700" : customer.segment === 'regular' ? G.accent : G.muted
                            }}>
                              {customer.segment.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Customer Insights */}
              {customerAnalytics.insights && customerAnalytics.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    💡 Customer Insights
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {customerAnalytics.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
          
          {/* PRODUCT ANALYTICS TAB */}
          {activeTab === 'products' && productAnalytics && (
            <div>
              {/* Top Products */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  🏆 Top Products by Revenue
                </div>
                <div style={{ 
                  border: `1px solid ${G.border}`,
                  borderRadius: 8,
                  overflow: "hidden"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: G.ink }}>
                      <tr>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Rank
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Product
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Revenue
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Qty Sold
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Avg Price
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Times Ordered
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {productAnalytics.topProducts.map((product, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderTop: `1px solid ${G.border}`,
                            background: idx % 2 === 0 ? G.surface : G.surface2
                          }}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: idx < 3 ? "#FFD700" : G.muted }}>
                            #{idx + 1}
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 500 }}>
                            {product.name}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>
                            {formatCurrency(product.revenue)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted }}>
                            {product.quantitySold}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace" }}>
                            {formatCurrency(product.averagePrice)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted }}>
                            {product.timesOrdered}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Product Metrics */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Total Products
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink }}>
                    {productAnalytics.metrics.totalProducts}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Total Quantity Sold
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink }}>
                    {productAnalytics.metrics.totalQuantitySold}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Avg Revenue/Product
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink }}>
                    {formatCurrency(productAnalytics.metrics.averageRevenuePerProduct)}
                  </div>
                </Card>
              </div>
              
              {/* Product Insights */}
              {productAnalytics.insights && productAnalytics.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    💡 Product Insights
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {productAnalytics.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
          
          {/* PAYMENT ANALYTICS TAB */}
          {activeTab === 'payments' && paymentAnalytics && (
            <div>
              <Card G={G}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  💳 Payment Method Breakdown
                </div>
                <div style={{ 
                  border: `1px solid ${G.border}`,
                  borderRadius: 8,
                  overflow: "hidden"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: G.ink }}>
                      <tr>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Payment Method
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Count
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Amount
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          % of Total
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Avg Transaction
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentAnalytics.breakdown.map((method, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderTop: `1px solid ${G.border}`,
                            background: idx % 2 === 0 ? G.surface : G.surface2
                          }}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 500 }}>
                            {method.method}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted }}>
                            {method.count}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>
                            {formatCurrency(method.amount)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right" }}>
                            <span style={{
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 12,
                              fontWeight: 600,
                              background: G.accentBg,
                              color: G.accent
                            }}>
                              {method.percentage}%
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace" }}>
                            {formatCurrency(method.averageTransaction)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
          
          {/* PROFITABILITY TAB */}
          {activeTab === 'profitability' && profitability && (
            <div>
              {/* Revenue Overview */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(4, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Gross Revenue
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.ink }}>
                    {formatCurrency(profitability.revenue.gross)}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Net Revenue
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.success }}>
                    {formatCurrency(profitability.revenue.net)}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Collection Rate
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: profitability.revenue.collectionRate > 80 ? G.success : G.danger }}>
                    {profitability.revenue.collectionRate.toFixed(1)}%
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Outstanding
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.muted }}>
                    {formatCurrency(profitability.revenue.outstanding)}
                  </div>
                </Card>
              </div>
              
              {/* Deductions */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  Deductions & Adjustments
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                      💸 Total Discounts Given
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.danger, marginBottom: 4 }}>
                      {formatCurrency(profitability.deductions.discounts.total)}
                    </div>
                    <div style={{ fontSize: 12, color: G.muted }}>
                      {profitability.deductions.discounts.percentage}% of gross revenue
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                      🔄 Total Refunds
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.danger, marginBottom: 4 }}>
                      {formatCurrency(profitability.deductions.refunds.total)}
                    </div>
                    <div style={{ fontSize: 12, color: G.muted }}>
                      {profitability.deductions.refunds.percentage}% of collected
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Tax Information */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  Tax Collection
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                      Total Tax
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink, marginBottom: 4 }}>
                      {formatCurrency(profitability.tax.total)}
                    </div>
                    <div style={{ fontSize: 12, color: G.muted }}>
                      {profitability.tax.percentage}% of gross revenue
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                      Tax Collected
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.success, marginBottom: 4 }}>
                      {formatCurrency(profitability.tax.collected)}
                    </div>
                    <div style={{ fontSize: 12, color: G.muted }}>
                      From paid invoices
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Profitability Insights */}
              {profitability.insights && profitability.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    💡 Profitability Insights
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {profitability.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
          
          {/* FORECAST TAB */}
          {activeTab === 'forecast' && forecast && (
            <div>
              {/* Forecast Summary */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: 16, 
                marginBottom: 16 
              }}>
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Forecast Method
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: G.accent, marginBottom: 8 }}>
                    {forecast.method.replace(/-/g, ' ').toUpperCase()}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    {forecast.confidence.description}
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Expected Growth
                  </div>
                  <div style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: forecast.summary.expectedGrowth > 0 ? G.success : forecast.summary.expectedGrowth < 0 ? G.danger : G.muted,
                    marginBottom: 8 
                  }}>
                    {getTrendIcon(forecast.summary.expectedGrowth > 0 ? 'up' : forecast.summary.expectedGrowth < 0 ? 'down' : 'stable')} {forecast.summary.expectedGrowth.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    per period
                  </div>
                </Card>
                
                <Card G={G} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    Confidence Level
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: G.ink, marginBottom: 8 }}>
                    {forecast.confidence.level}%
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    {forecast.confidence.level >= 75 ? "High confidence" : forecast.confidence.level >= 60 ? "Medium confidence" : "Low confidence"}
                  </div>
                </Card>
              </div>
              
              {/* Forecast Data */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  🔮 Revenue Forecast (Next {forecast.forecast.length} Periods)
                </div>
                <div style={{ 
                  border: `1px solid ${G.border}`,
                  borderRadius: 8,
                  overflow: "hidden"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: G.ink }}>
                      <tr>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Period
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Forecasted Revenue
                        </th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 11, fontWeight: 600, color: G.surface, textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Method
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {forecast.forecast.map((prediction, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderTop: `1px solid ${G.border}`,
                            background: idx % 2 === 0 ? G.surface : G.surface2
                          }}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 500 }}>
                            {prediction.period}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, fontSize: 16 }}>
                            {formatCurrency(prediction.revenue)}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right", color: G.muted, fontSize: 11 }}>
                            {prediction.method}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Seasonality */}
              {forecast.seasonality.detected && (
                <Card G={G} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    📊 Seasonality Detected
                  </div>
                  <div style={{ fontSize: 14, color: G.ink2, marginBottom: 12 }}>
                    Pattern: <strong>{forecast.seasonality.pattern}</strong>
                  </div>
                  <div style={{ fontSize: 14, color: G.ink2 }}>
                    Strength: <strong>{forecast.seasonality.strength.toFixed(0)}%</strong>
                  </div>
                </Card>
              )}
              
              {/* Forecast Insights */}
              {forecast.insights && forecast.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    💡 Forecast Insights
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {forecast.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
          
          {/* OPERATIONS TAB */}
          {activeTab === 'operations' && operationalMetrics && (
            <div>
              {/* Invoicing Metrics */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  📄 Invoicing Metrics
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Total Invoices</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {operationalMetrics.invoicing.total}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Average Value</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {formatCurrency(operationalMetrics.invoicing.averageValue)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Median Value</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {formatCurrency(operationalMetrics.invoicing.medianValue)}
                    </div>
                  </div>
                </div>
                
                <div style={{ 
                  borderTop: `1px solid ${G.border}`, 
                  marginTop: 16, 
                  paddingTop: 16 
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: G.ink, marginBottom: 12 }}>
                    Invoice Frequency
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Per Day</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>
                        {operationalMetrics.invoicing.frequency.perDay}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Per Week</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>
                        {operationalMetrics.invoicing.frequency.perWeek}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Per Month</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>
                        {operationalMetrics.invoicing.frequency.perMonth}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Payment Metrics */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  💳 Payment Performance
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Total Payments</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {operationalMetrics.payments.total}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Avg Cycle Time</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: operationalMetrics.payments.averageCycleTime < 30 ? G.success : G.danger }}>
                      {operationalMetrics.payments.averageCycleTime} days
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Median Cycle Time</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {operationalMetrics.payments.medianCycleTime} days
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Efficiency Metrics */}
              <Card G={G} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                  ⚡ Efficiency Metrics
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Invoices per Customer</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {operationalMetrics.efficiency.invoicesPerCustomer}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Items per Invoice</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: G.ink }}>
                      {operationalMetrics.efficiency.averageItemsPerInvoice}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 6 }}>Fully Paid Rate</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: parseFloat(operationalMetrics.efficiency.fullyPaidRate) > 70 ? G.success : G.danger }}>
                      {operationalMetrics.efficiency.fullyPaidRate}%
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Operational Insights */}
              {operationalMetrics.insights && operationalMetrics.insights.length > 0 && (
                <Card G={G}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: G.ink, marginBottom: 16 }}>
                    💡 Operational Insights
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {operationalMetrics.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: G.surface2,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8,
                          padding: "14px 16px"
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          gap: 12 
                        }}>
                          <div style={{ 
                            fontSize: 20,
                            color: getInsightColor(insight.type)
                          }}>
                            {getInsightIcon(insight.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 600, 
                              color: G.ink, 
                              marginBottom: 4 
                            }}>
                              {insight.title}
                            </div>
                            <div style={{ 
                              fontSize: 13, 
                              color: G.ink2, 
                              marginBottom: 8,
                              lineHeight: 1.5
                            }}>
                              {insight.message}
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: G.muted,
                              fontStyle: "italic"
                            }}>
                              💡 {insight.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}